import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, CheckCircle2, Circle, ExternalLink, Link2, Share2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import LegalDisclaimer from '../components/LegalDisclaimer';
import VideoStrip from './courses/VideoStrip';
import { coursesAPI } from '../services/api';

const ArticleLink = ({ article }) => {
  if (!article || !article.url) return null;
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 border border-purple-200 text-sm text-purple-900"
      data-testid="course-article-link"
    >
      <BookOpen className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span className="flex-1">
        <span className="block font-medium">{article.title}</span>
        {article.publisher && (
          <span className="text-xs text-purple-700">{article.publisher}</span>
        )}
      </span>
      <ExternalLink className="w-3.5 h-3.5 mt-1" />
    </a>
  );
};

const QuickCourseView = ({ course }) => (
  <>
    {course.summary && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-gray-900">Overview</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-700 whitespace-pre-line">
          {course.summary}
        </CardContent>
      </Card>
    )}

    {Array.isArray(course.key_takeaways) && course.key_takeaways.length > 0 && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-gray-900">Key takeaways</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700">
            {course.key_takeaways.map((k) => (
              <li key={k}>{k}</li>
            ))}
          </ul>
        </CardContent>
      </Card>
    )}

    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-gray-900">YouTube videos</CardTitle>
      </CardHeader>
      <CardContent>
        <VideoStrip videos={course.videos} testIdPrefix="quick-video" />
      </CardContent>
    </Card>

    {Array.isArray(course.articles) && course.articles.length > 0 && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-gray-900">Curated articles</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {course.articles.map((a, idx) => (
            <ArticleLink key={a.url || idx} article={a} />
          ))}
        </CardContent>
      </Card>
    )}
  </>
);

const LessonCard = ({ moduleIdx, lesson, isComplete, onToggle, busy }) => (
  <div
    className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
    data-testid={`lesson-${lesson.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold">
          Module {moduleIdx + 1} · Lesson {lesson.id?.replace(/^m\d+l/, '')}
        </p>
        <h4 className="text-base font-semibold text-gray-900 mt-0.5">
          {lesson.title}
        </h4>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onToggle(lesson.id)}
        disabled={busy}
        className={
          isComplete
            ? 'text-emerald-600 hover:text-emerald-700'
            : 'text-gray-400 hover:text-purple-600'
        }
        data-testid={`lesson-toggle-${lesson.id}`}
      >
        {isComplete ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : (
          <Circle className="w-5 h-5" />
        )}
      </Button>
    </div>

    {lesson.summary && (
      <p className="text-sm text-gray-700 whitespace-pre-line">{lesson.summary}</p>
    )}

    {Array.isArray(lesson.key_takeaways) && lesson.key_takeaways.length > 0 && (
      <ul className="list-disc pl-5 space-y-1 text-xs text-gray-600">
        {lesson.key_takeaways.map((k) => (
          <li key={k}>{k}</li>
        ))}
      </ul>
    )}

    <VideoStrip videos={lesson.videos} testIdPrefix={`lesson-video-${lesson.id}`} />

    <ArticleLink article={lesson.article} />
  </div>
);

const FullCourseView = ({ course, completedSet, onToggle, busyLesson }) => (
  <>
    {course.overview && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-gray-900">Overview</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-700 whitespace-pre-line">
          {course.overview}
        </CardContent>
      </Card>
    )}

    {(course.modules || []).map((mod, mi) => (
      <Card key={mod.title || `module-${mi}`} data-testid={`module-${mi + 1}`}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-purple-600 font-semibold">
                Module {mi + 1}
              </p>
              <CardTitle className="text-gray-900">{mod.title}</CardTitle>
              {mod.objective && (
                <p className="text-sm text-gray-600 mt-1">{mod.objective}</p>
              )}
            </div>
            <Badge variant="outline" className="text-[10px]">
              {(mod.lessons || []).length} lessons
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {(mod.lessons || []).map((lesson) => (
            <LessonCard
              key={lesson.id}
              moduleIdx={mi}
              lesson={lesson}
              isComplete={completedSet.has(lesson.id)}
              busy={busyLesson === lesson.id}
              onToggle={onToggle}
            />
          ))}
        </CardContent>
      </Card>
    ))}
  </>
);

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyLesson, setBusyLesson] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await coursesAPI.get(courseId);
      setDoc(d);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        toast.error('Course not found');
        navigate('/courses');
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [courseId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const completedSet = useMemo(
    () => new Set(doc?.completed_lessons || []),
    [doc],
  );

  const handleToggle = async (lessonId) => {
    setBusyLesson(lessonId);
    try {
      const next = !completedSet.has(lessonId);
      const r = await coursesAPI.toggleLesson(courseId, lessonId, next);
      setDoc((prev) =>
        prev ? { ...prev, completed_lessons: r.completed_lessons } : prev,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update progress');
    } finally {
      setBusyLesson(null);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const info = await coursesAPI.share(courseId);
      setShareInfo(info);
      const url = `${window.location.origin}/share/courses/${info.share_token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard!');
      } catch (_) {
        toast.success('Share link created');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create share link');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="max-w-4xl mx-auto px-4 text-gray-500">Loading course…</div>
      </div>
    );
  }
  if (!doc) return null;

  const course = doc.course || {};
  const lessonCount = (course.modules || []).reduce(
    (acc, m) => acc + ((m.lessons || []).length || 0),
    0,
  );
  const progress =
    lessonCount > 0
      ? Math.round((completedSet.size / lessonCount) * 100)
      : 0;

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">
        <Link
          to="/courses"
          className="inline-flex items-center text-sm text-gray-500 hover:text-purple-600"
          data-testid="course-back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to courses
        </Link>

        <Card className="bg-gradient-to-br from-[#07070d] via-[#0a0a14] to-[#0d0d1f] border-purple-500/20 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <Badge className="bg-purple-600/30 text-purple-200 border border-purple-500/30">
                  {doc.mode === 'quick' ? 'Quick search' : 'Full course'}
                </Badge>
                <h1 className="text-2xl md:text-3xl font-bold mt-2">
                  {course.title || doc.topic}
                </h1>
                <p className="text-sm text-gray-300 mt-1">Topic: {doc.topic}</p>
              </div>
              <div className="flex items-center gap-2">
                {course.level && (
                  <Badge className="bg-cyan-600/30 text-cyan-200 border border-cyan-500/30">
                    {course.level}
                  </Badge>
                )}
                <Button
                  onClick={handleShare}
                  disabled={sharing}
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
                  data-testid="course-share-btn"
                  size="sm"
                >
                  <Share2 className="w-4 h-4 mr-1.5" />
                  {sharing ? 'Creating link…' : 'Share course'}
                </Button>
              </div>
            </div>
            {shareInfo && (
              <div
                className="mt-4 flex items-center gap-2 text-xs bg-white/5 border border-white/10 rounded-lg px-3 py-2"
                data-testid="course-share-link-box"
              >
                <Link2 className="w-3.5 h-3.5 text-cyan-300" />
                <span className="text-gray-300 truncate flex-1">
                  {`${window.location.origin}/share/courses/${shareInfo.share_token}`}
                </span>
                <button
                  onClick={async () => {
                    const url = `${window.location.origin}/share/courses/${shareInfo.share_token}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      toast.success('Copied!');
                    } catch (_) {
                      toast.error('Could not copy');
                    }
                  }}
                  className="text-cyan-300 hover:text-cyan-200 text-xs"
                  data-testid="course-share-copy-btn"
                >
                  Copy
                </button>
              </div>
            )}
            {doc.mode === 'full' && lessonCount > 0 && (
              <div className="mt-5">
                <div className="flex justify-between text-xs text-gray-300 mb-1">
                  <span>Progress</span>
                  <span>
                    {completedSet.size} / {lessonCount} lessons · {progress}%
                  </span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 via-purple-500 to-fuchsia-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {doc.mode === 'quick' ? (
          <QuickCourseView course={course} />
        ) : (
          <FullCourseView
            course={course}
            completedSet={completedSet}
            onToggle={handleToggle}
            busyLesson={busyLesson}
          />
        )}

        <LegalDisclaimer variant="general" />
      </div>
    </div>
  );
};

export default CourseDetail;
